// helpers/DatastoreHelper.js

import { DataStore, syncExpression } from "@aws-amplify/datastore";
import { Hub } from "aws-amplify/utils";

import {
  Accounts,
  Projects,
  Forms,
  Submissions,
  Users,
  Events,
  Logs,
  Assets,
} from "../models";

import { PermissionType, ActionType } from "../Enums.js";
import * as TokenHelpers from "./TokenHelpers";
import * as LogHelpers from "./LogHelpers";
import { enqueueDiffNotifications, enqueueLiveEventNotifications } from "./MessageHelpers.js";

/**
 * Manual function to unsubscribe from all subscriptions
 */
export function unsubscribeSubscriptions({ subscriptionsRef, logSubscriptionsRef, logger }) {
  const timestamp = new Date().toISOString();
  if (logSubscriptionsRef?.current) {
    console.log(`[${timestamp}] US: unsubscribeSubscriptions called`, {
      hasRef: !!subscriptionsRef,
      currentLength: subscriptionsRef?.current?.length ?? null,
      refTag: subscriptionsRef?.__subscriptionsTag ?? null,
    });
  }

  try {
    if (subscriptionsRef?.current?.length > 0) {
      if (logSubscriptionsRef?.current) console.log(`[${timestamp}] US: Unsubscribing ${subscriptionsRef.current.length} subscriptions`);
      subscriptionsRef.current.forEach((sub) => sub.unsubscribe());
      subscriptionsRef.current = [];
      if (logSubscriptionsRef?.current) logger?.log?.("US:", "Subscriptions manually cleared.");
    } else {
      if (logSubscriptionsRef?.current) console.log(`[${timestamp}] US: No subscriptions to clear`);
    }
  } catch (err) {
    console.error(`[${timestamp}] US: Error while unsubscribing subscriptions:`, err);
    logger?.error?.("US:", "Error while unsubscribing subscriptions:", err);
  }
}

/**
 * Stop and clear DataStore with timeout protection (mirrors your App.js logic).
 */
export async function stopAndClearDataStore({ subscriptionsRef, logSubscriptionsRef, logger }) {
  const timestamp = new Date().toISOString();

  // Mandatory bookend - always visible so you can tell this step ran, regardless of the flag.
  console.log(`[${timestamp}] SCD: ===== STOP AND CLEAR DATASTORE START =====`);
  if (logSubscriptionsRef?.current) {
    console.log(`[${timestamp}] SCD: DataStore.isSyncing:`, DataStore.isSyncing);
    console.log(`[${timestamp}] SCD: Subscriptions count:`, subscriptionsRef?.current?.length ?? 0);
  }

  unsubscribeSubscriptions({ subscriptionsRef, logSubscriptionsRef, logger });

  try {
    if (DataStore.isSyncing) {
      if (logSubscriptionsRef?.current) console.log(`[${timestamp}] SCD: Stopping sync...`);
      await DataStore.stopSync();
      if (logSubscriptionsRef?.current) console.log(`[${timestamp}] SCD: Sync stopped successfully`);
    } else {
      if (logSubscriptionsRef?.current) console.log(`[${timestamp}] SCD: DataStore not syncing, skip stopSync`);
    }

    if (logSubscriptionsRef?.current) logger?.log?.("HAS-SDH-SCD:", "Stopping DataStore...");

    await DataStore.stop();

    if (logSubscriptionsRef?.current) logger?.log?.("HAS-SDH-SCD:", "DataStore stopped.");

    if (logSubscriptionsRef?.current) console.log(`[${timestamp}] SCD: Waiting 1000ms...`);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    if (logSubscriptionsRef?.current) console.log(`[${timestamp}] SCD: Wait complete`);

  } catch (innerError) {
    console.error(`[${timestamp}] SCD: Error during DataStore stop:`, innerError);
    throw new Error("An error occurred during DataStore stop: " + (innerError?.message || innerError));
  }

  try {
    if (logSubscriptionsRef?.current) logger?.log?.("HAS-SDH-SCD:", "Clearing DataStore...");

    const clearPromise = DataStore.clear();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("DataStore.clear() timeout")), 60000)
    );

    await Promise.race([clearPromise, timeoutPromise]);

    if (logSubscriptionsRef?.current) logger?.log?.("HAS-SDH-SCD:", "DataStore cleared.");

  } catch (innerError) {
    console.error(`[${timestamp}] SCD: Error during DataStore clear:`, innerError);
    if (innerError?.message === "DataStore.clear() timeout") {
      throw new Error("Timeout occurred during DataStore.clear()");
    }
    throw new Error("An error occurred during DataStore stop or clear: " + (innerError?.message || innerError));
  }

  // Mandatory bookend - pairs with the START marker above.
  console.log(`[${timestamp}] SCD: ===== STOP AND CLEAR DATASTORE COMPLETE =====`);
}

function sliceModelUser(u) {
  if (!u) return null;
  return {
    id: u?.id,
    email: u?.email,
    givenName: u?.givenName,
    familyName: u?.familyName,
    language: u?.language,
    projectAdminLen: typeof u?.projectAdmin === "string" ? u.projectAdmin.length : null,
    createdAt: u?.createdAt,
  };
}

function sliceEnhancedUser(u) {
  if (!u) return null;
  return {
    id: u?.id,
    email: u?.email,
    adminLevel: u?.adminLevel,
    adminTokens: u?.adminTokens,
  };
}

/**
 * Configure sync expressions.
 * - Uses ONLY the plain model user (state `user`) for Users sync scoping.
 * - Accepts appUser separately for logging/debug only.
 */
export function configureDataStoreSync({
  currentAccountToken,
  currentProjectToken,

  // plain model user from state
  user,

  // enhanced user (may include non-model fields)
  appUser,

  logStartDataStoreHydrationRef,
  logger,
}) {
  const timestamp = new Date().toISOString();
  const accountToken = currentAccountToken;
  const defaultProjectToken = currentProjectToken;

  console.log(`[${timestamp}] CDS: ===== CONFIGURE DATASTORE SYNC START =====`);

  if (logStartDataStoreHydrationRef?.current) {
    logger?.log?.("HAS-SDH-CDS:", "Account Token:", accountToken);
    logger?.log?.("HAS-SDH-CDS:", "Default Project Token:", defaultProjectToken);
    logger?.log?.("HAS-SDH-CDS:", "User ID:", user?.id);
    logger?.log?.("HAS-SDH-CDS:", "User (model slice):", sliceModelUser(user));
    logger?.log?.("HAS-SDH-CDS:", "appUser (enhanced slice):", sliceEnhancedUser(appUser));
  }

  DataStore.configure({
    syncExpressions: [
      syncExpression(Accounts, () => (accounts) => accounts.id.ne(0)),
      syncExpression(Projects, () => (projects) => projects.accountToken.eq(accountToken)),
      syncExpression(Forms, () => (forms) => forms.projectToken.eq(defaultProjectToken)),
      syncExpression(Submissions, () => (submissions) => submissions.projectToken.eq(defaultProjectToken)),

      // IMPORTANT: scope Users by the plain model user's id
      syncExpression(Users, () => (users) => users.id.eq(user?.id)),

      syncExpression(Events, () => (events) => events.projectToken.eq(defaultProjectToken)),
      syncExpression(Logs, () => (logs) => logs.projectToken.eq(defaultProjectToken)),
      syncExpression(Assets, () => (assets) => assets.projectToken.eq(defaultProjectToken)),
    ],
  });

  if (logStartDataStoreHydrationRef?.current) {
    logger?.log?.("HAS-SDH-CDS:", "Sync Expressions configured for Project Token:", defaultProjectToken);
  }
  console.log(`[${timestamp}] CDS: ===== CONFIGURE DATASTORE SYNC COMPLETE =====`);
}

let globalSetupRunId = 0;
let globalSubId = 0;
let globalSubscriptionsRefTagId = 0;

/**
 * setupSubscriptions moved here (full behaviour preserved).
 * Updated:
 * - Accept both `user` (plain model state) and `appUser` (enhanced)
 * - Ensure `setUser` is ONLY ever called with a plain model user (Users model shape)
 * - ADDED: Filtering of stale events based on app start time
 */
export function setupSubscriptions({
  // models
  DataStoreModelObserve = DataStore.observe, // test seam; default to DataStore.observe

  // setters
  setAllSubmissions,
  setProjects,
  setLogs,
  setAssets,
  setUser,
  setTickerItems,
  setNotifications,

  // state + deps
  user, // plain model user from state
  appUser, // enhanced user
  currentProjectToken,
  reviewingTokens,
  currentViewRef,

  // refs
  subscriptionsRef,
  skipNextUpdateForId, // Set
  pendingBatchConfirmations, // Set - ids from a bulk op (e.g. hide-many) awaiting AppSync confirmation
  suppressNextDerivedRecompute, // { current: boolean } - one-shot skip for the next derived-effect recompute
  logSubscriptionsRef,

  // logger
  logger,
}) {
  const setupRunId = ++globalSetupRunId;
  const timestamp = new Date().toISOString();
  const setupStartTime = Date.now();

  // Mandatory bookend - always visible so you can tell subscription setup ran.
  console.log(`[${timestamp}] SS: ===== SETUP SUBSCRIPTIONS START =====`);
  if (logSubscriptionsRef?.current) {
    console.log(`[${timestamp}] SS: setupRunId:`, setupRunId);
    console.log(`[${timestamp}] SS: setupStartTime:`, setupStartTime);
  }

  // Tag the subscriptionsRef owner so we can detect ref replacement/remount patterns
  if (subscriptionsRef && !subscriptionsRef.__hasSubscriptionsTag) {
    subscriptionsRef.__hasSubscriptionsTag = true;
    subscriptionsRef.__subscriptionsTag = `subref-${++globalSubscriptionsRefTagId}`;
  }

  const subLog = (...args) => {
    if (!logSubscriptionsRef?.current) return;
    try {
      logger?.log?.(...args);
    } catch {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  };

  subLog("HAS-SS:", "setupSubscriptions called", {
    setupRunId,
    refTag: subscriptionsRef?.__subscriptionsTag ?? null,
    hasUser: !!user,
    userId: user?.id,
    hasAppUser: !!appUser,
    appUserId: appUser?.id,
    logSubscriptionsRef: !!logSubscriptionsRef?.current,
    skipNextUpdateForIdSize: skipNextUpdateForId?.size ?? null,
  });

  function userLogSlice(u) {
    if (!u) return null;

    const adminTokensKeys =
      u?.adminTokens?.tokens && typeof u.adminTokens.tokens === "object"
        ? Object.keys(u.adminTokens.tokens)
        : u?.adminTokens && typeof u.adminTokens === "object"
          ? Object.keys(u.adminTokens)
          : [];

    return {
      id: u.id,
      email: u.email,
      adminLevel: u.adminLevel,
      updatedAt: u.updatedAt,
      _version: u._version,
      _lastChangedAt: u._lastChangedAt,
      projectAdminLen: typeof u.projectAdmin === "string" ? u.projectAdmin.length : null,
      adminTokensAccountCount: adminTokensKeys.length,
      adminTokensAccounts: adminTokensKeys,
    };
  }

  // User subscription handler (Users model)
  function handleUsersChange(recordChange, meta) {
    try {
      const { opType, element } = recordChange || {};
      const elementId = element?.id;

      subLog("HAS-SS:USERS:", "Users change received", {
        setupRunId,
        refTag: subscriptionsRef?.__subscriptionsTag ?? null,
        subId: meta?.subId ?? null,
        opType,
        elementId,
        elementUpdatedAt: element?.updatedAt,
        elementLastChangedAt: element?._lastChangedAt,
        userId: user?.id,
        hasUser: !!user,
      });

      // Gate on plain model user id (not enhanced)
      if (!user || user.id !== elementId) {
        subLog("HAS-SS:USERS:", "Users change ignored by gate", {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId: meta?.subId ?? null,
          reason: !user ? "no user in closure" : "user.id !== element.id",
          userId: user?.id,
          elementId,
        });
        return;
      }

      // Check what changed
      const projectAdminChanged = element?.projectAdmin !== user.projectAdmin;
      const dawnSnapshotChanged = element?.dawnSnapshot !== user.dawnSnapshot;
      const lastLoginChanged = element?.lastLogin !== user.lastLogin;
      const lastActivityChanged = element?.lastActivity !== user.lastActivity;

      // If nothing relevant changed, skip
      if (!projectAdminChanged && !dawnSnapshotChanged && !lastLoginChanged && !lastActivityChanged) {
        subLog("HAS-SS:USERS:", "Skipping user update — no relevant fields changed.", {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId: meta?.subId ?? null,
          elementId,
          elementProjectAdminLen:
            typeof element?.projectAdmin === "string" ? element.projectAdmin.length : null,
          userProjectAdminLen: typeof user?.projectAdmin === "string" ? user.projectAdmin.length : null,
        });
        return;
      }

      // Log what changed
      const changedFields = [];
      if (projectAdminChanged) changedFields.push("projectAdmin");
      if (dawnSnapshotChanged) changedFields.push("dawnSnapshot");
      if (lastLoginChanged) changedFields.push("lastLogin");
      if (lastActivityChanged) changedFields.push("lastActivity");

      subLog("HAS-SS:USERS:", "User fields changed", {
        setupRunId,
        refTag: subscriptionsRef?.__subscriptionsTag ?? null,
        subId: meta?.subId ?? null,
        elementId,
        changedFields,
      });

      // Handle projectAdmin change
      if (projectAdminChanged) {
        subLog("HAS-SS:USERS:", "projectAdmin changed — parsing tokens", {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId: meta?.subId ?? null,
          elementId,
        });
        const parsed = TokenHelpers.parseProjectAdmin(element.projectAdmin);

        const tokenAccounts =
          parsed?.tokens?.tokens && typeof parsed.tokens.tokens === "object"
            ? Object.keys(parsed.tokens.tokens)
            : [];

        subLog("HAS-SS:USERS:", "parseProjectAdmin result", {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId: meta?.subId ?? null,
          elementId,
          tokenAccountCount: tokenAccounts.length,
          tokenAccounts,
        });
      }

      // Handle dawnSnapshot change
      if (dawnSnapshotChanged && element?.dawnSnapshot) {
        subLog("HAS-SS:USERS:", "dawnSnapshot changed — processing", {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId: meta?.subId ?? null,
          elementId,
          hasDawnSnapshot: !!element.dawnSnapshot,
        });

        try {
          const snapshot = typeof element.dawnSnapshot === "string"
            ? JSON.parse(element.dawnSnapshot)
            : element.dawnSnapshot;

          // Check if this is a recent snapshot (last 5 minutes)
          const generatedAt = new Date(snapshot.lastSummaryGeneratedAt);
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

          if (generatedAt > fiveMinutesAgo) {
            console.log("🌅 NEW DAWN SNAPSHOT DETECTED!");
            console.log("═".repeat(80));
            console.log("%c📝 AI SUMMARY:", "font-weight: bold; font-size: 14px; color: #4ECDC4;");
            console.log(snapshot.lastSummary);
            console.log("");
            
            console.log("%c📊 RAW METRICS:", "font-weight: bold; font-size: 14px; color: #95E1D3;");
            console.table({
              "Total Submissions": snapshot.lastSnapshot?.raw?.total_submissions || 0,
              "Submissions Today": snapshot.lastSnapshot?.raw?.submissions_today || 0,
              "Complete Submissions": snapshot.lastSnapshot?.raw?.complete_submissions || 0,
              "Total Reviews": snapshot.lastSnapshot?.raw?.total_reviews || 0,
              "Complete Reviews": snapshot.lastSnapshot?.raw?.complete_reviews || 0,
              "Total Reviewers": snapshot.lastSnapshot?.raw?.total_reviewers || 0,
            });
            console.log("");
            
            if (snapshot.lastSnapshot?.raw?.submissions_by_topic) {
              console.log("%c📋 SUBMISSIONS BY TOPIC:", "font-weight: bold; font-size: 14px; color: #AA96DA;");
              console.table(snapshot.lastSnapshot.raw.submissions_by_topic);
              console.log("");
            }
            
            if (snapshot.lastSnapshot?.raw?.submissions_by_format) {
              console.log("%c🎯 SUBMISSIONS BY FORMAT:", "font-weight: bold; font-size: 14px; color: #FCBAD3;");
              console.table(snapshot.lastSnapshot.raw.submissions_by_format);
              console.log("");
            }
            
            if (snapshot.lastSnapshot?.raw?.reviewer_details?.length > 0) {
              console.log("%c👥 REVIEWER DETAILS:", "font-weight: bold; font-size: 14px; color: #6C5CE7;");
              console.table(
                snapshot.lastSnapshot.raw.reviewer_details.map(r => ({
                  Name: r.name || "Unknown",
                  Email: r.email || "N/A",
                  Assigned: r.assigned_count || 0,
                  Completed: r.completed_count || 0,
                  Progress: r.assigned_count > 0 
                    ? `${Math.round((r.completed_count / r.assigned_count) * 100)}%`
                    : "0%",
                }))
              );
              console.log("");
            }
            
            console.log("%c⏰ Generated:", "font-weight: bold; color: #FFFFD2;", snapshot.lastSummaryGeneratedAt);
            console.log("═".repeat(80));
            console.log("Full snapshot:", snapshot);
            console.log("═".repeat(80));
          } else {
            subLog("HAS-SS:USERS:", "dawnSnapshot is not recent, skipping display", {
              setupRunId,
              refTag: subscriptionsRef?.__subscriptionsTag ?? null,
              subId: meta?.subId ?? null,
              elementId,
              generatedAt: snapshot.lastSummaryGeneratedAt,
              age: Math.round((Date.now() - generatedAt.getTime()) / 1000 / 60) + " minutes",
            });
          }
        } catch (err) {
          subLog("HAS-SS:USERS:", "Error parsing dawnSnapshot", {
            setupRunId,
            refTag: subscriptionsRef?.__subscriptionsTag ?? null,
            subId: meta?.subId ?? null,
            elementId,
            error: err.message,
          });
        }
      }

      // IMPORTANT: setUser must receive a plain model user object.
      const updatedModelUser = {
        ...user,
        ...(projectAdminChanged && { projectAdmin: element.projectAdmin }),
        ...(dawnSnapshotChanged && { dawnSnapshot: element.dawnSnapshot }),
        ...(lastLoginChanged && { lastLogin: element.lastLogin }),
        ...(lastActivityChanged && { lastActivity: element.lastActivity }),
      };

      subLog("HAS-SS:USERS:", "User BEFORE (model slice)", sliceModelUser(user));
      subLog("HAS-SS:USERS:", "User ELEMENT (model slice)", sliceModelUser(element));
      subLog("HAS-SS:USERS:", "User AFTER (model slice)", sliceModelUser(updatedModelUser));

      // Also show enhanced slice for context (no state update here)
      subLog("HAS-SS:USERS:", "appUser (enhanced slice)", userLogSlice(appUser));

      setUser(updatedModelUser);

      subLog("HAS-SS:USERS:", "setUser called from Users subscription", {
        setupRunId,
        refTag: subscriptionsRef?.__subscriptionsTag ?? null,
        subId: meta?.subId ?? null,
        elementId,
        updatedUserId: updatedModelUser?.id,
        changedFields,
      });
    } catch (err) {
      // Log and return rather than re-throw: this runs inside the Users subscription's
      // next() callback, which has no try/catch of its own. A thrown error there
      // terminates the underlying observable per the Observable contract, silently
      // killing the Users subscription for the rest of the session. One bad record
      // shouldn't take down all future Users updates.
      console.error("HAS-SS:USERS: Error updating user state:", err);
    }
  }

  function subscribeToModel(model, type, setState, { pendingBatchConfirmations, suppressNextDerivedRecompute } = {}) {
    const subId = ++globalSubId;

    subLog("HAS-SS:", `Subscribing to ${type}`, {
      setupRunId,
      refTag: subscriptionsRef?.__subscriptionsTag ?? null,
      subId,
    });

    const sub = DataStoreModelObserve(model).subscribe({
      next(recordChange) {
        const { opType, element } = recordChange || {};
        const id = element?.id;
        const eventLastChangedAt = element?._lastChangedAt || 0;

        // CRITICAL: Check if this is a stale event from before subscription setup
        const ageMs = setupStartTime - eventLastChangedAt;
        const isStale = eventLastChangedAt > 0 && ageMs > 5000; // 5 second grace period

        // Filter out stale events from hibernation replay
        if (isStale) {
          subLog("HAS-SS:", `STALE EVENT FILTERED: ${type}/${id}`, {
            setupRunId,
            subId,
            ageMs,
            ageDays: (ageMs / (1000 * 60 * 60 * 24)).toFixed(2),
          });
          return;
        }

        subLog("HAS-SS:", `${type} subscription callback`, {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId,
          opType,
          id,
          version: element?._version,
          lastChangedAt: element?._lastChangedAt,
        });

        // Skip the very first local-echo UPDATE for this ID
        if (opType === ActionType.UPDATE && skipNextUpdateForId.has(id)) {
          subLog("HAS-SS:", `Skipping local echo UPDATE for ${type}/${id}`, {
            setupRunId,
            refTag: subscriptionsRef?.__subscriptionsTag ?? null,
            subId,
          });
          skipNextUpdateForId.delete(id);
          return;
        }

        // Bulk-batch confirmation, redundant except on the last one
        if (opType === ActionType.UPDATE && pendingBatchConfirmations?.has(id)) {
          pendingBatchConfirmations.delete(id);
          if (suppressNextDerivedRecompute) {
            suppressNextDerivedRecompute.current = pendingBatchConfirmations.size > 0;
          }
        }

        setState((prev) => {
          let next;
          switch (opType) {
            case ActionType.INSERT:
              next = [...prev, element];
              break;
            case ActionType.UPDATE:
              next = prev.map((i) => (i.id === id ? element : i));
              enqueueDiffNotifications({
                type,
                prevElement: prev.find((i) => i.id === id),
                nextElement: element,
                setNotifications,
                currentView: currentViewRef.current,
                logger,
              });
              break;
            case ActionType.DELETE:
              next = prev.filter((i) => i.id !== id);
              break;
            default:
              next = prev;
          }

          subLog("HAS-SS:", `${type} state updated`, {
            setupRunId,
            refTag: subscriptionsRef?.__subscriptionsTag ?? null,
            subId,
            length: next?.length ?? null,
          });

          return next;
        });

        enqueueLiveEventNotifications({
          changeType: type,
          recordChange,
          setTickerItems,
          setNotifications,
          currentProjectToken,
          reviewingTokens,
          currentView: currentViewRef.current,
          logger,
          appUser, // pass enhanced user (even if currently unused)
        });
      },
      error(e) {
        console.error(`[${timestamp}] SS-ERROR: Error in ${type} subscription:`, e);
        subLog("HAS-SS:", `Error in ${type} subscription:`, {
          setupRunId,
          refTag: subscriptionsRef?.__subscriptionsTag ?? null,
          subId,
          error: e,
        });
      },
    });

    subscriptionsRef.current.push(sub);

    subLog("HAS-SS:", `${type} subscription added`, {
      setupRunId,
      refTag: subscriptionsRef?.__subscriptionsTag ?? null,
      subId,
      total: subscriptionsRef.current.length,
    });
  }

  // Wire them up
  unsubscribeSubscriptions({ subscriptionsRef, logSubscriptionsRef, logger });
  subscriptionsRef.current = [];

  subLog("HAS-SS:", "Cleared existing subscriptions.", {
    setupRunId,
    refTag: subscriptionsRef?.__subscriptionsTag ?? null,
  });

  subscribeToModel(Submissions, "Submissions", setAllSubmissions, { pendingBatchConfirmations, suppressNextDerivedRecompute });
  subscribeToModel(Projects, "Projects", setProjects);
  subscribeToModel(Logs, "Logs", setLogs);
  subscribeToModel(Assets, "Assets", setAssets);

  subLog("HAS-SS:", "Subscribing to Users (special handler)", {
    setupRunId,
    refTag: subscriptionsRef?.__subscriptionsTag ?? null,
  });

  const usersSubId = ++globalSubId;

  const userSub = DataStore.observe(Users).subscribe({
    next(recordChange) {
      const { opType, element } = recordChange || {};

      subLog("HAS-SS:USERS:", "Users subscription callback", {
        setupRunId,
        refTag: subscriptionsRef?.__subscriptionsTag ?? null,
        subId: usersSubId,
        opType,
        elementId: element?.id,
        elementVersion: element?._version,
        elementLastChangedAt: element?._lastChangedAt,
      });

      handleUsersChange(recordChange, { subId: usersSubId });
    },
    error(e) {
      console.error(`[${timestamp}] SS-ERROR: Error in Users subscription:`, e);
      subLog("HAS-SS:", "Error in Users subscription:", {
        setupRunId,
        refTag: subscriptionsRef?.__subscriptionsTag ?? null,
        subId: usersSubId,
        error: e,
      });
    },
  });

  subscriptionsRef.current.push(userSub);

  subLog("HAS-SS:", "Subscriptions setup complete", {
    setupRunId,
    refTag: subscriptionsRef?.__subscriptionsTag ?? null,
    count: subscriptionsRef.current.length,
  });

  // Mandatory bookend - pairs with the START marker above.
  console.log(`[${timestamp}] SS: ===== SETUP SUBSCRIPTIONS COMPLETE =====`);
  if (logSubscriptionsRef?.current) console.log(`[${timestamp}] SS: Total subscriptions: ${subscriptionsRef.current.length}`);

  // Return cleanup (optional, mirrors your earlier pattern)
  return () => {
    subLog("HAS-SS:", "Cleaning up subscriptions...", {
      setupRunId,
      refTag: subscriptionsRef?.__subscriptionsTag ?? null,
    });

    subscriptionsRef.current.forEach((s) => s.unsubscribe());
    subscriptionsRef.current = [];

    subLog("HAS-SS:", "Subscriptions cleaned up.", {
      setupRunId,
      refTag: subscriptionsRef?.__subscriptionsTag ?? null,
    });
  };
}

/**
 * listenForReadySignal moved here (full behaviour preserved).
 * Uses appUser (enhanced) for admin filtering decisions.
 */
export async function listenForReadySignal({
  // setters
  setAccounts,
  setProjects,
  setForms,
  setAllSubmissions,
  setEvents,
  setLogs,
  setAssets,

  // deps
  appUser,

  // refs
  bDevRef,
  logFetchProjectDataRef,

  // logger
  logger,
}) {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] LRS: ===== LISTEN FOR READY SIGNAL START =====`);

  return new Promise((resolve) => {
    const readyListener = Hub.listen("datastore", async (hubData) => {
      try {
        const { event, data } = hubData.payload;
        const eventTimestamp = new Date().toISOString();

        if (logFetchProjectDataRef?.current) {
          const modelName = data?.model?.name ?? data?.model ?? null;
          console.log(`[${eventTimestamp}] LRS: Hub event received:`, modelName ? `${event} (${modelName})` : event);
        }

        if (event !== "ready") return;

        if (logFetchProjectDataRef?.current) {
          console.log(`[${eventTimestamp}] LRS: READY event received - starting data fetch`);
          logger?.log?.("HAS-SDH-FPD:", "Listener triggered by READY event");
        }

        try {
          if (logFetchProjectDataRef?.current) console.log(`[${eventTimestamp}] LRS: Querying DataStore models...`);

          const [
            accountsData,
            projectsData,
            formsData,
            submissionsData,
            eventsData,
            logsData,
            assetsData,
          ] = await Promise.all([
            DataStore.query(Accounts),
            DataStore.query(Projects),
            DataStore.query(Forms),
            DataStore.query(Submissions),
            DataStore.query(Events),
            DataStore.query(Logs),
            DataStore.query(Assets),
          ]);

          console.log(`[${eventTimestamp}] LRS: DataStore queries complete:`, {
            accounts: accountsData.length,
            projects: projectsData.length,
            forms: formsData.length,
            submissions: submissionsData.length,
            events: eventsData.length,
            logs: logsData.length,
            assets: assetsData.length,
          });

          let filteredAccounts = [];

          if (projectsData.length > 0) {
            if (logFetchProjectDataRef?.current) {
              logger?.log?.("HAS-SDH-FPD:", "First Hydration Stage (check projects data)");
              logger?.log?.("HAS-SDH-FPD:", "accountsData:", accountsData);
              logger?.log?.("HAS-SDH-FPD:", "projectsData:", projectsData);
              logger?.log?.("HAS-SDH-FPD:", "formsData:", formsData);
              logger?.log?.("HAS-SDH-FPD:", "submissionsData:", submissionsData);
              logger?.log?.("HAS-SDH-FPD:", "eventsData:", eventsData);
              logger?.log?.("HAS-SDH-FPD:", "logsData:", logsData);
              logger?.log?.("HAS-SDH-FPD:", "assetsData:", assetsData);
            }

            if (logFetchProjectDataRef?.current) logger?.log?.("HAS-SDH-FPD:", "Admin Tokens", appUser?.adminTokens);

            // Support either shape:
            // - appUser.adminTokens.tokens (preferred)
            // - appUser.adminTokens (legacy/object map)
            const adminTokensMap =
              appUser?.adminTokens?.tokens && typeof appUser.adminTokens.tokens === "object"
                ? appUser.adminTokens.tokens
                : appUser?.adminTokens && typeof appUser.adminTokens === "object"
                  ? appUser.adminTokens
                  : {};

            const adminAccounts = Object.keys(adminTokensMap);

            filteredAccounts =
              appUser?.adminLevel === PermissionType.SUPER_ADMIN
                ? accountsData
                : accountsData.filter((acct) => adminAccounts.includes(acct.token));

            if (logFetchProjectDataRef?.current) logger?.log?.("HAS-SDH-FPD:", "Filtered Accounts", filteredAccounts);

            const filteredProjects = projectsData.filter((project) => {
              const tokens = adminTokensMap?.[project.accountToken] ?? [];
              if (!Array.isArray(tokens)) return false;
              return tokens.some((t) => String(t).replace(/\*$/, "") === project.token);
            });

            if (logFetchProjectDataRef?.current) logger?.log?.("HAS-SDH-FPD:", "Filtered Projects", filteredProjects);

            setAccounts(filteredAccounts);
            setProjects(filteredProjects);
            setForms(formsData);
            setAllSubmissions(submissionsData);
            setEvents(eventsData);
            setLogs(logsData);
            setAssets(assetsData);

            if (logFetchProjectDataRef?.current) console.log(`[${eventTimestamp}] LRS: State updated with filtered data`);

            // Save to localStorage in dev mode (same behaviour)
            try {
              if (process.env.NODE_ENV === "development" && bDevRef?.current) {
                if (logFetchProjectDataRef?.current) logger?.log?.("HAS-SDH-FPD:", "Saving data to localStorage");
                localStorage.setItem("accountsData", JSON.stringify(filteredAccounts));
                localStorage.setItem("projectsData", JSON.stringify(projectsData));
                localStorage.setItem("formsData", JSON.stringify(formsData));
                localStorage.setItem("submissionsData", JSON.stringify(submissionsData));
                localStorage.setItem("eventsData", JSON.stringify(eventsData));
                localStorage.setItem("logsData", JSON.stringify(logsData));
                localStorage.setItem("assetsData", JSON.stringify(assetsData));
              }
            } catch (err) {
              console.error(`[${eventTimestamp}] LRS: Error saving to localStorage:`, err);
              if (logFetchProjectDataRef?.current) logger?.error?.("HAS-SDH-FPD:", "Error saving data to localStorage:", err);
              throw new Error("Failed to save data to localStorage.");
            }
          } else {
            const errMsg = `
              SDH: Got a Ready event but no data:
              <p>Account Data: ${JSON.stringify(filteredAccounts)}</p>
              <p>Project Data: ${JSON.stringify(projectsData)}</p>
              <p>Forms Data: ${JSON.stringify(formsData)}</p>
              <p>Submission Data: ${JSON.stringify(submissionsData)}</p>
              <p>Events Data: ${JSON.stringify(eventsData)}</p>
              <p>Log Data: ${JSON.stringify(logsData)}</p>
              <p>Assets Data: ${JSON.stringify(assetsData)}</p>
            `;
            console.warn(`[${eventTimestamp}] LRS: READY but no projects data:`, errMsg);
            if (logFetchProjectDataRef?.current) logger?.log?.("HAS-SDH-FPD:", errMsg);
          }
        } catch (err) {
          console.error(`[${eventTimestamp}] LRS: Error querying DataStore:`, err);
          if (logFetchProjectDataRef?.current) logger?.error?.("HAS-SDH-FPD:", "Error querying DataStore:", err);
          throw new Error("Error during DataStore queries.");
        }

        console.log(`[${eventTimestamp}] LRS: ===== LISTEN FOR READY SIGNAL COMPLETE =====`);
        resolve();

        // Remove listener
        readyListener();
      } catch (err) {
        const errTimestamp = new Date().toISOString();
        console.error(`[${errTimestamp}] LRS: Error during READY event handling:`, err);
        if (logFetchProjectDataRef?.current) logger?.error?.("HAS-SDH-FPD:", "Error during READY event handling:", err);
      }
    });
  });
}

/**
 * startDataStoreHydration moved here (full behaviour preserved)
 */
export async function startDataStoreHydration({
  // refs
  bDevRef,
  logStartDataStoreHydrationRef,
  logSubscriptionsRef,

  // deps
  currentAccountToken,
  currentProjectToken,
  user,
  appUser,

  // setters for localStorage shortcut
  setAccounts,
  setForms,
  setProjects,
  setAllSubmissions,

  // other handlers injected
  configureSyncFn, // () => void
  listenReadyFn, // () => Promise<void>

  // subscription refs (for stop/clear)
  subscriptionsRef,

  // logger + UI error handling hooks
  logger,
  setDataFetched,
  handleError, // handleError
}) {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] SDH: ===== START DATASTORE HYDRATION =====`);

  try {
    // dev localStorage shortcut
    if (process.env.NODE_ENV === "development" && bDevRef?.current) {
      if (logStartDataStoreHydrationRef?.current) {
        console.log(`[${timestamp}] SDH: Checking localStorage for cached data...`);
        logger?.log?.("HAS-SDH:", "Trying to get data from local storage");
      }

      const storedAccounts = localStorage.getItem("accountsData");
      const storedForms = localStorage.getItem("formsData");
      const storedProjects = localStorage.getItem("projectsData");
      const storedSubmissions = localStorage.getItem("submissionsData");

      if (storedAccounts && storedForms && storedProjects && storedSubmissions) {
        setAccounts(JSON.parse(storedAccounts));
        setForms(JSON.parse(storedForms));
        setProjects(JSON.parse(storedProjects));
        setAllSubmissions(JSON.parse(storedSubmissions));
        if (logStartDataStoreHydrationRef?.current) logger?.log?.("HAS-SDH:", "***Loaded ALL model data from localStorage.***");
        console.log(`[${timestamp}] SDH: ===== HYDRATION COMPLETE (from localStorage) =====`);
        return;
      } else {
        if (logStartDataStoreHydrationRef?.current) console.log(`[${timestamp}] SDH: No cached data found, proceeding with DataStore`);
      }
    } else {
      if (logStartDataStoreHydrationRef?.current) logger?.log?.("HAS-SDH:", "process.env.NODE_ENV:", process.env.NODE_ENV);
      if (logStartDataStoreHydrationRef?.current) logger?.log?.("HAS-SDH:", "bDev.current:", bDevRef?.current);
    }

    // Stop and clear
    await stopAndClearDataStore({
      subscriptionsRef,
      logSubscriptionsRef,
      logger,
    });

    // Configure sync
    configureSyncFn();

    if (logStartDataStoreHydrationRef?.current) logger?.log?.("HAS-SDH:", "Starting DataStore...");

    await DataStore.start();

    if (logStartDataStoreHydrationRef?.current) logger?.log?.("HAS-SDH:", "DataStore started and is hydrating. Waiting for ready signal.");

    await listenReadyFn();

    console.log(`[${timestamp}] SDH: ===== DATASTORE HYDRATION COMPLETE =====`);
  } catch (error) {
    const errTimestamp = new Date().toISOString();
    console.error(`[${errTimestamp}] SDH: ERROR during DataStore setup:`, error);
    logger?.error?.("HAS-SDH:", "Error during DataStore setup:", error);

    if (typeof setDataFetched === "function") {
      setDataFetched(true);
    }

    const errMsg =
      "<p>HAS-SDH: Got an error during the main configuration of datastore and consequent listening for ready signal: " +
      (error?.message || error) +
      "</p>";

    if (typeof handleError === "function") {
      handleError("Datastore error: " + errMsg, appUser || user);
    }

    alert(
      "An error occurred during application initialization. Click OK to refresh the page. If you see this message again, please check for the admin page open elsewhere and/or contact support."
    );

    window.location.reload();
  }
}

/**
 * Single orchestration call for the useEffect in App.js:
 * - hydrate
 * - setup subscriptions
 *
 * IMPORTANT:
 * - `user` is the plain Users model object from state (source of truth for setUser)
 * - `appUser` is the enhanced user (used for admin filtering + UI decisions)
 */
export async function hydrateAndSetupProjectScope({
  // deps
  currentAccountToken,
  currentProjectToken,
  user,
  appUser,
  reviewingTokens,
  currentViewRef,

  // refs
  bDevRef,
  subscriptionsRef,
  skipNextUpdateForId,
  pendingBatchConfirmations,
  suppressNextDerivedRecompute,
  logStartDataStoreHydrationRef,
  logFetchProjectDataRef,
  logSubscriptionsRef,

  // setters
  setAccounts,
  setProjects,
  setForms,
  setAllSubmissions,
  setEvents,
  setLogs,
  setAssets,
  setUser,
  setTickerItems,
  setNotifications,
  setDataFetched,

  // logger + error hook
  logger,
  handleError,
}) {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] HASP: ===== HYDRATE AND SETUP PROJECT SCOPE START =====`);

  // configure + ready listener need closures with current tokens/user/appUser
  const configureSyncFn = () =>
    configureDataStoreSync({
      currentAccountToken,
      currentProjectToken,
      user,
      appUser,
      logStartDataStoreHydrationRef,
      logger,
    });

  const listenReadyFn = () =>
    listenForReadySignal({
      setAccounts,
      setProjects,
      setForms,
      setAllSubmissions,
      setEvents,
      setLogs,
      setAssets,
      appUser,
      bDevRef,
      logFetchProjectDataRef,
      logger,
    });

  await startDataStoreHydration({
    bDevRef,
    logStartDataStoreHydrationRef,
    logSubscriptionsRef,
    currentAccountToken,
    currentProjectToken,
    user,
    appUser,
    setAccounts,
    setForms,
    setProjects,
    setAllSubmissions,
    configureSyncFn,
    listenReadyFn,
    subscriptionsRef,
    logger,
    setDataFetched,
    handleError,
  });

  if (logStartDataStoreHydrationRef?.current) console.log(`[${timestamp}] HASP: Calling setupSubscriptions...`);
  setupSubscriptions({
    setAllSubmissions,
    setProjects,
    setLogs,
    setAssets,
    setUser,
    setTickerItems,
    setNotifications,

    user,
    appUser,
    currentProjectToken,
    reviewingTokens,
    currentViewRef,

    subscriptionsRef,
    skipNextUpdateForId,
    pendingBatchConfirmations,
    suppressNextDerivedRecompute,
    logSubscriptionsRef,
    logger,
  });
  if (logStartDataStoreHydrationRef?.current) console.log(`[${timestamp}] HASP: setupSubscriptions complete`);

  console.log(`[${timestamp}] HASP: ===== HYDRATE AND SETUP PROJECT SCOPE COMPLETE =====`);
}

/**
 * Bootstrap hydrate for the signed-in Users record only.
 * - Configures DataStore to sync ONLY the current user (Users by id)
 * - Starts DataStore and waits for Hub "datastore" ready event
 * - Queries Users and returns the single user record
 *
 * NOTE:
 * - Logs are returned as [] on purpose (project token not known yet)
 * - Caller is responsible for setUser/setLogs/localStorage
 */
export async function hydrateUserFromDataStore({
  userId,
  logger,
  logFetchUserRef, // optional ref { current: boolean } for logging gates
  signal, // optional AbortSignal for cancellation
}) {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}] HUFD: ===== HYDRATE USER FROM DATASTORE START =====`);

  if (!userId) throw new Error("hydrateUserFromDataStore: userId is required");

  const log = (...args) => {
    if (logFetchUserRef?.current) logger?.log?.(...args);
  };

  log("FU-HAS:", "userId:", userId);

  if (signal?.aborted) {
    throw new Error("hydrateUserFromDataStore: aborted before start");
  }

  log("FU-HAS:", "Configuring DataStore for Users-only bootstrap", { userId });

  // Users-only bootstrap config (others are always-false gates)
  DataStore.configure({
    syncExpressions: [
      syncExpression(Forms, () => (form) => form.token.eq(0)),
      syncExpression(Submissions, () => (submissions) => submissions.projectToken.eq(0)),
      syncExpression(Users, () => (users) => users.id.eq(userId)),
      syncExpression(Accounts, () => (accounts) => accounts.id.eq(0)),
      syncExpression(Projects, () => (projects) => projects.id.eq(0)),
      syncExpression(Logs, () => (logs) => logs.projectToken.eq("")),
      syncExpression(Assets, () => (assets) => assets.projectToken.eq("")),
      syncExpression(Events, () => (events) => events.id.eq(0)),
    ],
  });

  log("FU-HAS:", "Starting DataStore...");
  await DataStore.start();
  log("FU-HAS:", "DataStore started; waiting for READY");

  const user = await new Promise((resolve, reject) => {
    let unsubscribe = null;

    const abortHandler = () => {
      try {
        if (typeof unsubscribe === "function") unsubscribe();
      } catch {}
      reject(new Error("hydrateUserFromDataStore: aborted while waiting for READY"));
    };

    if (signal) signal.addEventListener("abort", abortHandler, { once: true });

    unsubscribe = Hub.listen("datastore", async (hubData) => {
      try {
        const { event } = hubData.payload || {};
        log("FU-HAS:", "Hub event:", event);

        if (event !== "ready") return;

        log("FU-HAS:", "READY received; querying Users");

        const userData = await DataStore.query(Users);
        log("FU-HAS:", `Users query returned ${userData?.length ?? 0} records`);

        if (!Array.isArray(userData) || userData.length === 0) {
          throw new Error(`hydrateUserFromDataStore: READY but no Users returned for id ${userId}`);
        }

        // remove listener so it can't fire again
        try {
          if (typeof unsubscribe === "function") unsubscribe();
        } catch {}

        log("FU-HAS:", "User found, resolving");
        resolve(userData[0]);
      } catch (err) {
        const errTimestamp = new Date().toISOString();
        console.error(`[${errTimestamp}] HUFD: Error in READY handler:`, err);
        try {
          if (typeof unsubscribe === "function") unsubscribe();
        } catch {}
        reject(err);
      } finally {
        if (signal) {
          try {
            signal.removeEventListener("abort", abortHandler);
          } catch {}
        }
      }
    });
  });

  console.log(`[${timestamp}] HUFD: ===== HYDRATE USER FROM DATASTORE COMPLETE =====`);
  // No project token yet => logs intentionally empty here
  return { user, logs: [] };
}